import type { EngineRuleRoomRate, EntitiesDataProps, HotelDetails, HotelResult, MetasearchResult, Occupancy, OriginalHotelResult, OriginalRoomRate, RoomRate } from '@niarab2c/frontend-commons/src/types/hotels';
import { distanceLatLon, printPercentage } from '@niaratech/niara-js-commons';
import defaultLocale from '@niaratech/niara-react-components/lib/util/defaultLocale';
import shallowEqual from 'fbjs/lib/shallowEqual';
import _omit from 'lodash/omit';
import _uniq from 'lodash/uniq';
import { AddonAvailabilityResult } from '@niarab2c/frontend-commons/src/types/addon';
import normalizeMealName from '../normalizeMealName';
import { calculateRemuneration, groupRoomRates, isSame, roomRateSorter } from './index';
const ROOM_RATES_KEYS = ['roomRates', 'notAvailableRoomRates'] as const;
type Params = {
  occupancy?: Occupancy;
  referencePoint?: {
    latitude;
    longitude;
  };
  entitiesData?: EntitiesDataProps;
  promoCode?: string;
  clientId?: string;
  selfBooking: boolean;
  b2c: boolean;
  personBalance?: number;
  personContractStatus?: string;
  engineRuleVersion: string;
  manualReservationId?: string;
  quotationToken?: string;
  quotationIndex?: number;
  locale?: string;
};
/**
 * Transforma um OriginalHotelResult em um HotelResult:
 * - preenche campos complementares em cada roomRate, como credential, hotelId, occupancy, promocode (normaliza room rates)
 * - determina melhor preço, melhor remuneração, agrupa room rates, etc (analyzeBestRoomRates)
 * - se passar oldHR, faz merge
 *
 * @param hotelResult
 * @param oldHR
 * @param params
 * @returns
 */

export function prepareHotelResult(hotelResult: Partial<OriginalHotelResult> | Partial<HotelResult>, oldHR?: null | HotelResult, params?: Params): HotelResult {
  let result: Partial<HotelResult> = {
    ...hotelResult,
    clientId: params?.clientId ?? hotelResult['clientId'] ?? oldHR?.['clientId']
  } as Partial<HotelResult>;
  ROOM_RATES_KEYS.forEach(key => {
    if (hotelResult[key] && hotelResult[key].find((rr: OriginalRoomRate | RoomRate) => !rr['_normalizedRoomRate'])) {
      // normalize room rates
      result[key] = result[key].map<any>(_rr => {
        if (_rr['_normalizedRoomRate']) {
          // já foi normalizado!
          return _rr;
        }
        const roomRate = prepareRoomRate({
          ..._rr,
          _normalizedRoomRate: true
        }, params, key, hotelResult);
        return roomRate;
      });
    }
  });
  if (!result._id && result.hotel?.id) {
    result._id = result.hotel.id;
  }

  // result.omniHotelId #178496
  if (!result.omniHotelId && result._id?.startsWith('HOTEL_OMNI_')) {
    result.omniHotelId = result._id;
  }
  if (result.hotel && result._id) {
    // monta result._hotelByHotelId
    result._hotelByHotelId = result._hotelByHotelId ?? {};
    result._hotelByHotelId[result._id] = result.hotel;
  }
  if (result.hotel?.soupIds || result.hotel?.id) {
    const soupIds = _uniq([result.hotel.id].concat(result.hotel.soupIds).filter(Boolean)); // garante o próprio hotel id no soupIds
    if (!isSame(soupIds, result._soupIds)) {
      result._soupIds = soupIds;
    }
  }
  if (!result.hotel?.cityName && result.hotel?.propertyId && params?.entitiesData?.properties && params?.entitiesData?.properties?.[result.hotel?.propertyId]) {
    result.hotel.cityName = params?.entitiesData?.properties?.[result.hotel?.propertyId]?.city;
  }
  if (oldHR) {
    const preferredHotel = [oldHR.hotel, hotelResult.hotel].sort((a, b) => a?.id?.startsWith('HOTEL_OMNI_') ? -1 : 0)?.[0]; // se um dos resultados é da Omnibees, dá preferência ao da Omnibees #178496
    result = {
      ...oldHR,
      ...result,
      notAvailableRoomRates: _uniq((oldHR?.notAvailableRoomRates || [])?.concat(result?.notAvailableRoomRates || [])),
      _id: oldHR._id,
      hotel: preferredHotel,
      omniHotelId: oldHR.omniHotelId ?? result.omniHotelId,
      _hotelByHotelId: result._hotelByHotelId && oldHR._hotelByHotelId ? Object.assign({}, oldHR._hotelByHotelId || {}, result._hotelByHotelId || {}) : oldHR._hotelByHotelId ?? result._hotelByHotelId
    };
    if (!shallowEqual(oldHR._soupIds, result._soupIds)) {
      result._soupIds = _uniq((oldHR._soupIds || []).concat(result._soupIds || []));
    }
    if (hotelResult.roomRates) {
      result.roomRates = [...(oldHR.roomRates || []).filter(or => (hotelResult.roomRates as Array<RoomRate | OriginalRoomRate>).filter(nr => or.id === nr.id).length === 0), ...result.roomRates];
    }
    //join no hotel details
    const _hotelDetails: HotelDetails = [oldHR, result].map(c => c && '_hotelDetails' in c && c._hotelDetails).filter(x => x).reduce((acc, v) => !acc ? v : Object.assign({
      images: !acc.images ? v.images : !v.images ? acc.images : acc.images.concat(v.images),
      guestRooms: !acc.guestRooms ? v.guestRooms : !v.guestRooms ? acc.guestRooms : acc.guestRooms.concat(v.guestRooms)
    }, acc, v), null);
    result._hotelDetails = _hotelDetails;

    // adicionando size e sizeMeasurement ao roomType
    result?.roomRates?.map(item => {
      const matchingRoom = oldHR._hotelDetails?.guestRooms?.find(room => room.id === item.roomType.id);
      if (matchingRoom && item?.roomType && item.roomType.size != matchingRoom.size) {
        item.roomType.size = matchingRoom.size; // pode dar erro se hotelResult veio do getState do react-redux
        item.roomType.sizeMeasurement = matchingRoom.sizeMeasurement;
      }
      return item;
    });

    //Setando award a partir do hotelDetails.rating quando o hotelDetails.rating não for null.
    //Coloca a regra de caso venha mais de 7, colocar null para replicar a logica em todos os lugares
    //#210825
    if (result?._hotelDetails?.rating != null && result?._hotelDetails?.rating <= 7) {
      const award = result?._hotelDetails?.rating <= 7 ? result?._hotelDetails?.rating : null;
      if (result?.hotel?.award !== award) {
        result.hotel = Object.assign({}, result.hotel, {
          award
        });
        result._hotelByHotelId[oldHR._id] = result.hotel; // pode dar erro se hotelResult veio do getState do react-redux
      }
    }
    if (result?.hotel?.award > 7) {
      const award = null;
      if (result?.hotel?.award !== award) {
        result.hotel = Object.assign({}, result.hotel, {
          award
        });
        result._hotelByHotelId[oldHR._id] = result.hotel; // pode dar erro se hotelResult veio do getState do react-redux
      }
    }
  }
  if (params?.referencePoint && result._rawDistance == null && result.hotel?.position) {
    //calcula distância linha reta entre hotel e ponto de referência
    const {
      latitude: lat1,
      longitude: lng1
    } = result.hotel.position;
    const {
      latitude: lat2,
      longitude: lng2
    } = params.referencePoint;
    result.hotel._rawDistance = Math.round(distanceLatLon(lat1, lng1, lat2, lng2));
    result._rawDistance = result.hotel._rawDistance;
  }
  if (result.roomRates?.length > 0 && result.roomRates !== oldHR?.roomRates) {
    //calcula e coloca em result propriedades como _bestPrice, roomRatesGrouped, etc
    const bestPrices = analyzeBestRoomRates(result.roomRates);
    Object.assign(result, bestPrices);
  }
  if (result.notAvailableRoomRates?.length > 0 && result.notAvailableRoomRates !== oldHR?.notAvailableRoomRates /* TODO Esta comparação não está funcionando - comparar com ids? */) {
    result.notAvailableRoomRates = [...result.notAvailableRoomRates].sort(roomRateSorter);
    const notAvailableRoomRatesGrouped = groupRoomRates(result.notAvailableRoomRates);
    result.notAvailableRoomRatesGrouped = notAvailableRoomRatesGrouped;
  }
  if (params?.entitiesData?.properties) {
    result.neighborhood = params.entitiesData.properties[result.hotel.propertyId]?.neighborhood ?? null;
    result.location = params.entitiesData.properties[result.hotel.propertyId]?.location ?? null;
  }
  result._boost = result?.hotel?.meta?.boost ?? Math.max(...[...result?.roomRates.map(rr => 'meta' in rr && rr?.meta?.boost).filter(x => x), 0]);

  // Preenche o texto e a logo do hotel favorito se vier na busca
  if (params && params?.entitiesData && params?.entitiesData?.favoriteHotelsRules && result?.hotel?.meta && result?.hotel?.meta?.favoriteHotelsRuleId) {
    const favoriteHotelsRuleId = result?.hotel?.meta?.favoriteHotelsRuleId;
    result.meta = {
      boost: result?._boost,
      favoriteHotelsRuleId: result?.hotel?.meta?.favoriteHotelsRuleId,
      recommendedBadge: params?.entitiesData?.favoriteHotelsRules[favoriteHotelsRuleId]?.recommendedBadge,
      recommendedText: params?.entitiesData?.favoriteHotelsRules[favoriteHotelsRuleId]?.recommendedText
    };
  }
  return result as HotelResult;
}
export const analyzeBestRoomRates = (roomRates: RoomRate[]): {
  _bestRemuneration: number;
  _bestRemunerationRoomRates: RoomRate[];
  _bestRemunerationRoomRate: RoomRate;
  _bestWithBreakfastRoomRates: RoomRate[];
  _bestWithBreakfastRoomRate: RoomRate;
  _bestRefundableRoomRates: RoomRate[];
  _bestRefundableRoomRate: RoomRate;
  _bestRemunerationValue: number;
  _bestRemunerationValueRoomRates: RoomRate[];
  _bestRemunerationValueRoomRate: RoomRate;
  roomRates: RoomRate[];
  roomRatesGrouped: RoomRate[][]; // TODO: refactoring para não usar mais este valor
  _bestPriceRoomRates: RoomRate[];
  _bestPriceRoomRate: RoomRate;
  _bestPrice: number;
  _bestPriceNight: number;
} => {
  let _bestRemuneration: number, _bestRemunerationRoomRates: RoomRate[], _bestRemunerationRoomRate: RoomRate, _bestWithBreakfastRoomRates: RoomRate[], _bestWithBreakfastRoomRate: RoomRate, _bestRefundableRoomRates: RoomRate[], _bestRefundableRoomRate: RoomRate, _bestRemunerationValue: number, _bestRemunerationValueRoomRates: RoomRate[], _bestRemunerationValueRoomRate: RoomRate;
  roomRates = roomRates ? [...roomRates].sort(roomRateSorter) : [];
  const roomRatesGrouped = groupRoomRates(roomRates);
  const _bestPriceRoomRates = roomRatesGrouped[0];
  const _bestPriceRoomRate = _bestPriceRoomRates && _bestPriceRoomRates[0];
  const _bestPrice = _bestPriceRoomRate?.priceComposition?.total?.value;
  const _bestPriceNight = _bestPriceRoomRate?.priceComposition?.nights?.[0]?.value;

  //buscar melhor comissão/markup
  for (const roomRateGroup of roomRatesGrouped) {
    const roomRate = roomRateGroup[0];
    const remuneration = roomRate?._remuneration;
    if (remuneration?.percentage - (_bestRemuneration || 0) > 0.001) {
      _bestRemuneration = remuneration.percentage;
      _bestRemunerationRoomRate = roomRate;
      _bestRemunerationRoomRates = roomRateGroup;
    }
    if (remuneration?.value - (_bestRemunerationValue || 0) > 0.01) {
      _bestRemunerationValue = remuneration.value;
      _bestRemunerationValueRoomRate = roomRate;
      _bestRemunerationValueRoomRates = roomRateGroup;
    }
  }
  if (_bestRemuneration === 0) {
    _bestRemuneration = undefined;
    _bestRemunerationRoomRate = undefined;
    _bestRemunerationValue = undefined;
    _bestRemunerationValueRoomRate = undefined;
  }
  //busca melhor preço com café da manhã
  for (const roomRateGroup of roomRatesGrouped) {
    const roomRate = roomRateGroup[0];
    if (roomRate?.meal?.breakfast) {
      _bestWithBreakfastRoomRate = roomRate;
      _bestWithBreakfastRoomRates = roomRateGroup;
      break;
    }
  }
  //busca melhor preço reembolsável
  for (const roomRateGroup of roomRatesGrouped) {
    const roomRate = roomRateGroup[0];
    if (roomRate.cancelPolicy && !roomRate.cancelPolicy.nonRefundable && !roomRate.cancelPolicy.inPenalty) {
      _bestRefundableRoomRate = roomRate;
      _bestRefundableRoomRates = roomRateGroup;
      break;
    }
  }
  return {
    roomRates,
    roomRatesGrouped,
    _bestRemuneration,
    _bestRemunerationRoomRates,
    _bestRemunerationRoomRate,
    _bestPriceRoomRates,
    _bestPriceRoomRate,
    _bestPrice,
    _bestPriceNight,
    _bestWithBreakfastRoomRates,
    _bestWithBreakfastRoomRate,
    _bestRefundableRoomRates,
    _bestRefundableRoomRate,
    _bestRemunerationValue,
    _bestRemunerationValueRoomRates,
    _bestRemunerationValueRoomRate
  };
};
const prepareRoomRate = (roomRate: RoomRate, params: Params, key: 'notAvailableRoomRates' | 'roomRates', hotelResult: Partial<OriginalHotelResult> | Partial<HotelResult>) => {
  const selfBooking = params?.selfBooking;
  const b2c = params?.b2c;
  const personBalance = params?.personBalance;

  // garante roomRate.available
  roomRate.available = key === 'notAvailableRoomRates' ? false : true;
  // copia credential, occupancy e hotelId para cada roomRate
  roomRate.credential = hotelResult.credential;
  roomRate.hotelId = hotelResult.hotel.id; // usar hotelResult.hotel.id ao invés de result._id - é o id do hotel que originou a resposta
  roomRate.occupancy = params?.occupancy ?? roomRate?.occupancy;
  roomRate.promoCode = params?.promoCode ?? roomRate?.promoCode;
  roomRate.engineRuleVersion = params?.engineRuleVersion as '0' | '1' ?? roomRate?.engineRuleVersion ?? '0';
  roomRate.locale = params?.locale ?? roomRate.locale ?? defaultLocale();
  roomRate.manualReservationId = params?.manualReservationId ?? roomRate?.manualReservationId;
  roomRate.quotationToken = params?.quotationToken ?? roomRate?.quotationToken;
  roomRate.quotationIndex = params?.quotationIndex ?? roomRate?.quotationIndex;

  // adiciona os detalhes do addon (entitiesData) no addon do roomRate
  if (roomRate?.addons?.length > 0) {
    const addons = roomRate?.addons?.map(addon => ({
      ...addon,
      addon: params?.entitiesData?.addons?.[addon?.addonId]
    })) as Array<AddonAvailabilityResult>;
    roomRate.addons = addons;
  }
  if (key === 'notAvailableRoomRates' && (roomRate.priceComposition?.total?.value == 0 || roomRate.priceSortValue == 0)) {
    // vou apagar priceComposition deste cara, senão vai parecer que é uma tarifa gratuita  #215943
    roomRate.priceComposition = undefined;
    roomRate.priceSortValue = undefined;
  }

  // para pegar quantidade de dormitórios pelo nome
  const normalizedName = roomRate?.roomType?.name?.toLowerCase();
  const regex = /(\d+)\s*(bedroom|bedrooms|dormit[óo]rio|dormit[óo]rios|dorm|dorms)/i;
  const match = normalizedName?.match(regex);
  if (roomRate.roomType) {
    roomRate.roomType.bedroomCount = match && match[1] ? parseInt(match[1]) : null;
  }
  if (roomRate.engineRuleVersion != '1' && roomRate.payment?.paymentOptions?.find(o => o.extras) && roomRate.payment?.extras?.length > 0) {
    roomRate.payment.paymentOptions = roomRate.payment.paymentOptions.map(option => {
      if (option.extras) {
        return {
          ...option,
          extraOptions: roomRate.payment?.extras?.map(e => ({
            id: e.id?.toString(),
            type: e.type
          }))
        };
      } else {
        return option;
      }
    });
  }

  // Adiciona o priceComposition do paymentOptionsDetails no paymentOptions de acordo com o type
  if (roomRate.engineRuleVersion === '1' && roomRate?.paymentOptions && roomRate?.paymentOptionsDetails) {
    roomRate.paymentOptions = roomRate.paymentOptions.map(paymentOption => {
      const paymentOptionsDetails = roomRate.paymentOptionsDetails?.find(pod => pod?.paymentOptionsTypes?.includes(paymentOption?.internalType));
      return {
        ...paymentOption,
        priceComposition: paymentOptionsDetails?.priceComposition,
        loyaltyRedeemUnitText: paymentOption?.loyaltyRedeemId ? params?.entitiesData?.loyaltyRedeemRules?.[paymentOption?.loyaltyRedeemId]?.unitText : null
      };
    });
  }

  // loyaltyRedeems
  const loyaltyRedeems = roomRate.engineRuleVersion == '1' ? roomRate.loyaltyRedeems?.map(lr => {
    const loyaltyRedeemConfiguration = params?.entitiesData?.loyaltyRedeemRules?.[lr.id];
    const newLr: EngineRuleRoomRate['loyaltyRedeems'][number] = Object.assign(_omit(lr, 'minRemainingValue', 'remainingValue'), {
      fractionDigits: loyaltyRedeemConfiguration?.fractionDigits,
      type: loyaltyRedeemConfiguration?.type,
      unitText: loyaltyRedeemConfiguration?.unitText,
      moneyPointsOrder: loyaltyRedeemConfiguration?.moneyPointsOrder,
      remainingPrice: lr.remainingValue != null ? {
        value: lr.remainingValue,
        currency: roomRate.priceComposition?.total?.currency
      } : null,
      b2cAuthenticationRequired: loyaltyRedeemConfiguration?.b2cAuthenticationRequired
    });
    return newLr;
  }) : roomRate.businessRules?.map(br => br.loyaltyRedeems).flat(1)?.map(legacyLR => {
    const newLr: EngineRuleRoomRate['loyaltyRedeems'][number] = Object.assign(_omit(legacyLR, 'minRemainingValue'), {
      minRemainingPrice: legacyLR.minRemainingValue != null ? {
        value: legacyLR.minRemainingValue,
        currency: roomRate.priceComposition?.total?.currency
      } : null
    });
    return newLr;
  });
  roomRate.loyaltyRedeems = loyaltyRedeems;
  roomRate.loyaltyRewards = roomRate?.loyaltyRewards?.filter(lr => lr?.hideReward !== true);

  /** se personBalance não é nulo e todos os loyaltyredeems tem minPointsToBurn maior que o personBalance */
  if (personBalance != null && personBalance > 0 && loyaltyRedeems?.length > 0 /* ver bug #176798 */ && loyaltyRedeems?.every(redeem => redeem.minPointsToBurn > 0 && redeem.minPointsToBurn > personBalance)) {
    roomRate.available = false;
    roomRate.availabilityRestrictions = roomRate.availabilityRestrictions || [];
    roomRate.availabilityRestrictions.push({
      reasonMessage: "Não há pontos suficientes na carteira do hóspede"
    });
  }

  //calcula o productPrice (removendo as taxas do total value)
  if (roomRate.priceComposition?.total?.value != null) {
    const priceComposition = {
      ...roomRate.priceComposition
    };
    if (priceComposition.taxes?.value != null) {
      priceComposition.productPrice = {
        value: priceComposition?.total?.value - priceComposition?.taxes?.value,
        currency: priceComposition?.total?.currency
      };
    } else {
      priceComposition.productPrice = {
        value: priceComposition?.total?.value,
        currency: priceComposition?.total?.currency
      };
    }
    roomRate.priceComposition = priceComposition;

    //calcula a remuneração - TODO - calcular a remuneração diferente se for usuário do cliente
    roomRate._remuneration = b2c ? undefined : calculateRemuneration(roomRate, selfBooking) ?? roomRate._remuneration;
  } else {
    //Se não tiver total no priceComposition, apagar o priceComposition.
    roomRate.priceComposition = null;
  }

  //preparar propriedades que serão usadas em filtros
  roomRate._cancelPolicy = roomRate?.cancelPolicy?.nonRefundable ? 'NON_REFUNDABLE' : roomRate.cancelPolicy?.inPenalty ? 'IN_PENALTY' : 'REFUNDABLE';
  roomRate._rateType = [roomRate.priceComposition?.markup && 'MARKUP', roomRate.ratePlan && roomRate.ratePlan.type === 'COMM' && 'COMM', !roomRate.priceComposition?.markup && roomRate.ratePlan && roomRate.ratePlan.type === 'NET' && 'NET'].filter(x => x) as Array<'MARKUP' | 'COMM' | 'NET'>;
  roomRate.roomType = roomRate?.roomType && {
    ...roomRate?.roomType
  };
  roomRate._meal = roomRate?.meal && (roomRate?.meal.breakfast || roomRate.meal.lunch || roomRate.meal.dinner) ? normalizeMealName(roomRate?.meal.name) : null;
  roomRate._acceptedPayments = roomRate.engineRuleVersion == '1' ? roomRate.paymentOptions?.filter(op => op?.enabled != false)?.map(p => p.type) : roomRate.payment?.paymentOptions?.filter(op => op?.enabled != false)?.map(p => p.type);
  roomRate._outputPaymentTypes = roomRate.engineRuleVersion == '1' ? [] //Não determinar o tipos de pagamento ao fornecedor nessa versão #167340
  : roomRate.payment?.outputOptions?.filter(op => op?.enabled != false)?.map(p => p.type);
  if (roomRate.engineRuleVersion === '1' && roomRate.priceComposition && roomRate.paymentOptions?.[0]?.recurringPayments) {
    // Se veio months dentro do paymentOptions, salva ele no priceComposition e calcula o valor de desconto na mesma proporção do valor do mês
    // Utilizado pelo LongStay

    const totalDiscount = roomRate.priceComposition?.discountBreakdown?.reduce((sum, discount) => sum + (discount?.value || 0), 0);
    const totalValue = roomRate?.priceComposition?.total?.value ?? 0;
    const recurringPayments = roomRate.paymentOptions?.[0]?.recurringPayments?.map(month => {
      const discountValue = totalDiscount ? month?.value * totalDiscount / totalValue : null;
      return {
        ...month,
        discountValue
      };
    });
    const breakdownPerMonth = () => {
      const discountBreakdown = roomRate?.priceComposition?.discountBreakdown;
      const months = recurringPayments?.length ?? 0;
      if (!months || months <= 1) return discountBreakdown;
      return discountBreakdown?.map(discount => {
        const proportion = discount?.value / totalDiscount;
        const valuePerMonth = proportion * (totalDiscount / months);
        return {
          ...discount,
          value: valuePerMonth
        };
      });
    };
    roomRate.priceComposition.discountBreakdown = breakdownPerMonth();
    roomRate.priceComposition.recurringPayments = recurringPayments;
  }
  // calcula o desconto por forma de pagamento e adiciona no discountBreakdown como PAYMENT_DISCOUNT
  if (roomRate.engineRuleVersion == '1' && roomRate?.priceComposition?.total?.value && roomRate?.paymentOptions) {
    roomRate.priceComposition = addDiscountByPaymentOptionInDiscountBreakdown(roomRate?.priceComposition, roomRate?.paymentOptions);
  }

  // desconto pelo promocode - mostrar o promocode, mas não na cotação #232494
  const isQuotation = roomRate.quotationToken != null;
  if (!isQuotation && roomRate.priceComposition?.discountBreakdown?.length > 0) {
    roomRate.priceComposition.discountBreakdown.forEach(discount => {
      if (discount.code) {
        discount.description = discount.code;
      }
    });
  }
  if (params?.entitiesData && roomRate?.metasearch) {
    const metasearchRules = params.entitiesData.metaSearchRules?.[roomRate.metasearch.id];
    roomRate.metasearch = {
      ...roomRate.metasearch,
      slots: roomRate.metasearch.slots?.map((originalSlot): MetasearchResult['slots'][number] => {
        const metasearchRulesSlot = metasearchRules.slots?.find(slot => slot.id === originalSlot.id);
        const slotRoomRate = originalSlot?.item ? prepareRoomRate(originalSlot?.item, params, 'roomRates', hotelResult) : null;
        const loyaltyRedeems = roomRate.engineRuleVersion == '1' ? originalSlot?.loyaltyRedeems?.map(lr => {
          const loyaltyRedeemConfiguration = params?.entitiesData?.loyaltyRedeemRules?.[lr.id];
          const newLr: EngineRuleRoomRate['loyaltyRedeems'][number] = Object.assign(_omit(lr, 'minRemainingValue', 'remainingValue'), {
            fractionDigits: loyaltyRedeemConfiguration?.fractionDigits,
            type: loyaltyRedeemConfiguration?.type,
            unitText: loyaltyRedeemConfiguration?.unitText,
            moneyPointsOrder: loyaltyRedeemConfiguration?.moneyPointsOrder,
            remainingPrice: lr.remainingValue != null ? {
              value: lr.remainingValue,
              currency: roomRate.priceComposition?.total?.currency
            } : null,
            b2cAuthenticationRequired: loyaltyRedeemConfiguration?.b2cAuthenticationRequired
          });
          return newLr;
        }) : null;
        const loyaltyRewards = originalSlot?.loyaltyRewards?.filter(lr => lr?.hideReward !== true);
        const paymentOptions = slotRoomRate && 'paymentOptions' in slotRoomRate ? slotRoomRate?.paymentOptions : metasearchRulesSlot.ctaAction == 'NONE' || !metasearchRulesSlot.ctaAction ? null : metasearchRulesSlot.ctaAction == 'ADD_TO_CART' // rever - será esta a melhor forma de identificar o slot do resultado?
        ? roomRate.engineRuleVersion === '1' ? roomRate.paymentOptions : roomRate?.payment?.paymentOptions : null;
        const promotion = metasearchRulesSlot.sourceType === 'SEARCH' ? roomRate.promotion : null;
        const priceComposition = slotRoomRate?.priceComposition ?? addDiscountByPaymentOptionInDiscountBreakdown(originalSlot?.priceComposition, paymentOptions as OriginalRoomRate['paymentOptions']);

        // desconto pelo promocode - mostrar o promocode, mas não na cotação #232494 #233165
        if (!isQuotation && priceComposition?.discountBreakdown?.length > 0) {
          priceComposition.discountBreakdown.forEach(discount => {
            if (discount.code) {
              discount.description = discount.code;
            }
          });
        }
        return {
          ...metasearchRulesSlot,
          ...originalSlot,
          item: slotRoomRate,
          loyaltyRedeems,
          loyaltyRewards,
          badgeText: metasearchRulesSlot.badgeEnabled ? metasearchRulesSlot.badgeText : undefined,
          paymentOptions,
          promotion,
          priceComposition
        } as MetasearchResult['slots'][number] & RoomRate;
      })
    };
  }
  return roomRate;
};
const addDiscountByPaymentOptionInDiscountBreakdown = (priceComposition: RoomRate['priceComposition'], paymentOptions: OriginalRoomRate['paymentOptions']): RoomRate['priceComposition'] => {
  // calcula o desconto por forma de pagamento e adiciona no discountBreakdown como PAYMENT_DISCOUNT
  if (priceComposition?.total?.value && paymentOptions) {
    const defaultPrice = priceComposition?.total?.value;
    const defaultCurrency = priceComposition?.total?.currency;

    // Primeiro encontra todas as opções com preço menor
    const discountedOptions = paymentOptions.filter(paymentOption => {
      if (!paymentOption?.enabled) return false;
      if (!('priceComposition' in paymentOption)) return false;
      if (paymentOption?.priceComposition?.total?.currency !== defaultCurrency) return false;
      const curValue = paymentOption?.priceComposition?.total?.value;
      return curValue && curValue < defaultPrice;
    }).map(paymentOption => {
      const optionValue = paymentOption?.priceComposition?.total?.value;
      const discountPercentage = (defaultPrice - optionValue) / defaultPrice;
      const paymentOptionAlias = paymentOption?.alias || '';
      return {
        paymentOptionValue: optionValue,
        paymentOptionAlias,
        discountPercentage,
        formattedDiscountName: printPercentage(discountPercentage) + ' Desconto no ' + (paymentOption?.alias || ''),
        formattedDiscountDescription: `O desconto do ${paymentOptionAlias} será aplicado exclusivamente no pagamento.`
      };
    }).sort((a, b) => b.paymentOptionValue - a.paymentOptionValue); // Ordena do maior para o menor preço

    if (discountedOptions?.length > 0) {
      const previewDiscountBreakdown = [];
      discountedOptions.forEach(option => {
        // adiciona no começo do array
        previewDiscountBreakdown.unshift({
          originType: 'PAYMENT_DISCOUNT',
          currency: defaultCurrency,
          value: defaultPrice - option?.paymentOptionValue,
          description: option?.formattedDiscountName,
          additionalDescription: option?.formattedDiscountDescription
        });
      });
      priceComposition.previewDiscountBreakdown = previewDiscountBreakdown;
    }
  }
  return priceComposition as RoomRate['priceComposition'];
};